'use client';
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';

import { FaCheck } from 'react-icons/fa';
import { i18n } from '../../../i18n.config';
import { cn } from '@/lib/utils';
import { avertaSemibold } from '@/styles/fonts';
import {
  translateMissingLocale,
  translatePathname,
} from '@/utils/translatePathname';
import useCurrency from '@/hooks/useCurrency';
import { IURLDictionary } from '@/dictionaries/utils/url/types';
import { ISlugDictionary } from '@/dictionaries/utils/slugs/types';
import useGlobalContext from '@/hooks/useGlobalContext';

interface Option {
  value: string;
  flag?: string;
  label: string;
}

interface DropdownProps {
  title: string;
  options: Option[];
  selectedValue: string;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedCurrency: Dispatch<SetStateAction<string>>;
  setSelectedLang: Dispatch<SetStateAction<string>>;
  type: string;
  urlDict: IURLDictionary;
  slugsDict: ISlugDictionary;
  atFooter?: boolean;
}

const Dropdown: FC<DropdownProps> = ({
  title,
  options,
  selectedValue,
  setIsOpen,
  setSelectedCurrency,
  setSelectedLang,
  type,
  atFooter,
  slugsDict,
}) => {
  const [queryParams, setQueryParams] = useState('');

  const { saveLocalStorageCurrency } = useCurrency();
  const { isDynamicUrl, dynamicUrl } = useGlobalContext();

  const pathname = usePathname();
  const { refresh } = useRouter();
  const ref = useRef<HTMLDivElement>(null);

  async function handleSetCurrency(option: Option) {
    setSelectedCurrency(option.value);
    await saveLocalStorageCurrency(option.value);
    if (typeof window !== 'undefined') {
      refresh();
      window.location.reload();
    }
  }

  const redirectedPathName = (locale: string) => {
    if (!pathname) return '/';

    const pathnameIsMissingLocale = i18n.locales.every(
      (locale) =>
        !pathname.startsWith(`/${locale}/`) && pathname !== `/${locale}`,
    );

    if (pathnameIsMissingLocale) {
      const translations = translateMissingLocale(
        pathname,
        locale,
        slugsDict,
        selectedValue,
      );

      if (translations) {
        switch (Object.keys(translations).length) {
          case 1:
            if (
              (queryParams &&
                translations.slugOne &&
                translations.slugOne.includes('book-experience')) ||
              pathname.includes('book-villa')
            ) {
              return locale === 'en' ? '/' : `/${locale}`;
            }
            if (queryParams) {
              return translations.slugOne
                ? `/${locale}/${translations.slugOne}${queryParams}`
                : `/${locale}${queryParams}`;
            }
            return translations.slugOne
              ? `/${locale}/${translations.slugOne}`
              : `/${locale}`;
          case 2:
            if (queryParams) {
              return `/${locale}/${translations.slugOne}/${translations.slugTwo}${queryParams}`;
            }
            return `/${locale}/${translations.slugOne}/${translations.slugTwo}`;
          case 3:
            return `/${locale}/${translations.slugOne}/${translations.slugTwo}/${translations.slugThree}`;
          case 4:
            return `/${locale}/${translations.slugOne}/${translations.slugTwo}/${translations.slugThree}/${translations.slugFour}`;
          default:
            break;
        }
      }

      if (locale === i18n.defaultLocale) {
        if (queryParams) {
          return `${pathname}${queryParams}`;
        }
        return pathname;
      }

      if (queryParams) {
        return `/${locale}${pathname}${queryParams}`;
      }

      return `/${locale}${pathname}`;
    } else {
      const segments = pathname.split('/');
      const isHome = segments.length === 2;

      if (locale === i18n.defaultLocale && isHome) {
        return '/';
      } else if (locale !== i18n.defaultLocale && isHome) {
        segments[1] = locale;
        return segments.join('/');
      }

      const translations = translatePathname(
        pathname,
        locale,
        slugsDict,
        selectedValue,
      );

      if (
        (queryParams && translations?.includes('book-experience')) ||
        pathname.includes('book-villa')
      ) {
        return locale === 'en' ? '/' : `/${locale}`;
      }

      if (queryParams) {
        return `${translations}${queryParams}`;
      }

      return translations as string;
    }
  };

  useEffect(() => {
    const handleCloseDropwdown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        ref.current &&
        !ref.current.contains(target) &&
        !target.classList.contains(type)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleCloseDropwdown);

    return () => {
      document.removeEventListener('pointerdown', handleCloseDropwdown);
    };
  }, [setIsOpen, type]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const queryString = window.location.search;
      setQueryParams(queryString);
    }
  }, []);

  return (
    <div
      ref={ref}
      style={{ zIndex: 9999999 }}
      className={cn(
        'absolute top-[30px] desktop:-right-[20px] flex flex-col desktop:w-[364px] bg-white px-2 pt-4 pb-3 shadow-[0_1px_10px_0_rgba(0,0,0,.2)] z-[100]',
        {
          'desktop:-right-0 desktop:left-0': atFooter,
        },
      )}>
      <div className="px-4 py-2">
        <p
          className={cn(
            'text-sm font-semibold text-gray-900 uppercase text-nowrap',
            avertaSemibold.className,
          )}>
          {title}
        </p>
        <hr className="mt-4 border-t border-gray-300" />
      </div>

      <ul className="flex flex-col text-sm text-gray-900 uppercase list-none p-0">
        {options.map((option, index) => (
          <li
            key={option.value}
            onClick={async () => {
              type === 'lang'
                ? setSelectedLang(option.value)
                : await handleSetCurrency(option);
              setIsOpen(false);
            }}>
            {type === 'lang' ? (
              <Link
                href={
                  isDynamicUrl
                    ? dynamicUrl[option.value]
                    : redirectedPathName(option.value)
                }
                className={`flex items-center justify-between px-4 py-2 font-${
                  option.value === selectedValue ? 'semibold' : 'normal'
                } w-full cursor-pointer hover:bg-gray-300`}>
                <div className="flex gap-4">{option.label}</div>
                {option.value === selectedValue && (
                  <FaCheck className="text-header-green-check" />
                )}
              </Link>
            ) : (
              <div
                className={`flex items-center justify-between px-4 py-2 font-${
                  option.value === selectedValue ? 'semibold' : 'normal'
                } w-full cursor-pointer hover:bg-gray-300`}>
                <div className="flex gap-4 items-center">
                  {option.flag && (
                    <Image
                      src={option.flag}
                      alt={option.label}
                      width={21}
                      height={15}
                      sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                    />
                  )}
                  {option.label}
                </div>
                {option.value === selectedValue && (
                  <FaCheck className="text-header-green-check" />
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Dropdown;
